/**
 * OverviewTab displays broad campaign metrics: status breakdowns, publisher/country/
 * business-line distributions, asset status, and growth trends over time.
 *
 * Filters out ARCHIVED and DELETED campaigns to reduce noise. Uses bucketed time
 * queries (timeBucket: 'week'/'month') instead of separate date-range queries for
 * period-over-period comparison.
 */
import React, { useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import { DateTime } from 'luxon';
import { useTranslation } from 'src/arb-wrapper';
import { useQueryEntityMetricsQuery } from 'src/store/leela-api';
import { MetricCard } from 'src/components/entity-metrics/MetricCard';
import { StatusDonut } from 'src/components/entity-metrics/StatusDonut';
import { RankedBarChart } from 'src/components/entity-metrics/RankedBarChart';
import { GrowthLineChart } from 'src/components/entity-metrics/GrowthLineChart';
import {
  type TimePeriodValue,
  TIME_PERIOD_VALUES,
  TIME_PERIOD_KEYS,
  getDateRange,
} from 'src/components/entity-metrics/timePeriodUtils';

/** Exclude archived/deleted campaigns from all campaign queries */
const ACTIVE_STATUS_FILTER = [
  { field: 'desiredState.status', op: 'in', value: ['ACTIVE', 'PAUSED', 'SUSPENDED'] },
];

interface OverviewTabProps {
  chartPeriod: TimePeriodValue;
  onChartPeriodChange: (v: TimePeriodValue) => void;
  piePeriod: TimePeriodValue | 'all';
  onPiePeriodChange: (v: TimePeriodValue | 'all') => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  chartPeriod,
  onChartPeriodChange,
  piePeriod,
  onPiePeriodChange,
}) => {
  const { t } = useTranslation();

  const today = useMemo(() => DateTime.now().toISODate() ?? '', []);
  const last60Days = useMemo(() => DateTime.now().minus({ days: 60 }).toISODate() ?? '', []);

  const chartDateRange = useMemo(() => getDateRange(chartPeriod), [chartPeriod]);

  // Date range for pie charts (undefined = all time)
  const pieDateRange = useMemo(
    () => (piePeriod === 'all' ? undefined : getDateRange(piePeriod)),
    [piePeriod],
  );

  // Shared date params for breakdown queries
  const pieParams = pieDateRange ? { startDate: pieDateRange[0], endDate: pieDateRange[1] } : {};

  // --- Breakdown queries (all filtered to active statuses) ---

  const campaignStatusQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'desiredState.status',
    filters: ACTIVE_STATUS_FILTER,
    ...pieParams,
  });

  const countryQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'desiredState.productStrategies.country',
    filters: ACTIVE_STATUS_FILTER,
    ...pieParams,
  });

  const publisherQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'publisher',
    filters: ACTIVE_STATUS_FILTER,
    ...pieParams,
  });

  const businessLineQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'desiredState.productStrategies.businessLine',
    filters: ACTIVE_STATUS_FILTER,
    ...pieParams,
  });

  const assetStatusQuery = useQueryEntityMetricsQuery({
    entity: 'assets',
    groupBy: 'approvalStatus',
    ...pieParams,
  });

  // --- Time-bucketed queries for period-over-period comparison ---
  // Single query over 60 days bucketed by month → derive this-month vs prev-month
  const monthlyQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'publisher',
    timeBucket: 'month',
    filters: ACTIVE_STATUS_FILTER,
    startDate: last60Days,
    endDate: today,
  });

  // --- Growth chart query (also used for week-over-week derivation) ---
  const growthQuery = useQueryEntityMetricsQuery({
    entity: 'campaigns',
    groupBy: 'publisher',
    timeBucket: 'week',
    filters: ACTIVE_STATUS_FILTER,
    startDate: chartDateRange[0],
    endDate: chartDateRange[1],
  });

  // --- Derived metrics ---

  const totalCampaigns = useMemo(
    () => (campaignStatusQuery.data?.data ?? []).reduce((sum, d) => sum + d.value, 0),
    [campaignStatusQuery.data],
  );

  // Derive this-week / prev-week from the growth query (weekly bucketed)
  const { createdThisWeek, createdPrevWeek } = useMemo(() => {
    const buckets = growthQuery.data?.data ?? [];
    const dates = Array.from(new Set(buckets.map((d) => d.date ?? ''))).filter(Boolean).sort();
    if (dates.length === 0) return { createdThisWeek: 0, createdPrevWeek: 0 };
    const latestDate = dates[dates.length - 1];
    const prevDate = dates.length > 1 ? dates[dates.length - 2] : undefined;
    const thisWeekTotal = buckets.filter((d) => d.date === latestDate).reduce((sum, d) => sum + d.value, 0);
    const prevWeekTotal = prevDate ? buckets.filter((d) => d.date === prevDate).reduce((sum, d) => sum + d.value, 0) : 0;
    return { createdThisWeek: thisWeekTotal, createdPrevWeek: prevWeekTotal };
  }, [growthQuery.data]);

  // Derive this-month / prev-month from the monthly bucketed response
  const { createdThisMonth, createdPrevMonth } = useMemo(() => {
    const buckets = monthlyQuery.data?.data ?? [];
    const dates = Array.from(new Set(buckets.map((d) => d.date ?? ''))).filter(Boolean).sort();
    if (dates.length === 0) return { createdThisMonth: 0, createdPrevMonth: 0 };
    const latestDate = dates[dates.length - 1];
    const prevDate = dates.length > 1 ? dates[dates.length - 2] : undefined;
    const thisMonthTotal = buckets.filter((d) => d.date === latestDate).reduce((sum, d) => sum + d.value, 0);
    const prevMonthTotal = prevDate ? buckets.filter((d) => d.date === prevDate).reduce((sum, d) => sum + d.value, 0) : 0;
    return { createdThisMonth: thisMonthTotal, createdPrevMonth: prevMonthTotal };
  }, [monthlyQuery.data]);

  const totalAssets = useMemo(
    () => (assetStatusQuery.data?.data ?? []).reduce((sum, d) => sum + d.value, 0),
    [assetStatusQuery.data],
  );

  const countryCount = useMemo(
    () => new Set((countryQuery.data?.data ?? []).map((d) => d.label).filter(Boolean)).size,
    [countryQuery.data],
  );

  const publisherData = useMemo(() => (publisherQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [publisherQuery.data]);
  const countryData = useMemo(() => (countryQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [countryQuery.data]);
  const campaignStatusData = useMemo(() => (campaignStatusQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [campaignStatusQuery.data]);
  const businessLineData = useMemo(() => (businessLineQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [businessLineQuery.data]);
  const assetStatusData = useMemo(() => (assetStatusQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [assetStatusQuery.data]);

  const isError = campaignStatusQuery.isError || countryQuery.isError;

  return (
    <Column spacing="400">
      {isError && (
        <Row alignmentHorizontal="center">
          <Text color="error">{t('error_failed_to_load_campaign_data')}</Text>
        </Row>
      )}

      <Row spacing="400" widths="fill">
        <MetricCard isLoading={campaignStatusQuery.isLoading} label={t('metric_total_campaigns')} value={totalCampaigns} />
        <MetricCard isLoading={growthQuery.isLoading} label={t('metric_created_this_week')} previousValue={createdPrevWeek} value={createdThisWeek} />
        <MetricCard isLoading={monthlyQuery.isLoading} label={t('metric_created_this_month')} previousValue={createdPrevMonth} value={createdThisMonth} />
        <MetricCard isLoading={countryQuery.isLoading} label={t('metric_countries')} value={countryCount} />
        <MetricCard isLoading={assetStatusQuery.isLoading} label={t('metric_total_assets')} value={totalAssets} />
      </Row>

      <Row alignmentVertical="center" spacing="300">
        <Text type="b300">{t('label_breakdown_period')}</Text>
        <Select value={piePeriod} width={200} onChange={onPiePeriodChange}>
          <SelectOption label={t('label_all_time')} value="all" />
          {TIME_PERIOD_VALUES.map((value) => (
            <SelectOption key={value} label={t(TIME_PERIOD_KEYS[value])} value={value} />
          ))}
        </Select>
      </Row>

      <Row spacing="400" widths="fill">
        <StatusDonut data={publisherData} isLoading={publisherQuery.isLoading} title={t('chart_campaigns_by_publisher')} />
        <StatusDonut data={businessLineData} isLoading={businessLineQuery.isLoading} title={t('chart_by_business_line')} />
      </Row>

      <Row spacing="400" widths="fill">
        <StatusDonut data={campaignStatusData} isLoading={campaignStatusQuery.isLoading} title={t('chart_campaign_status')} />
        <StatusDonut data={assetStatusData} isLoading={assetStatusQuery.isLoading} title={t('chart_asset_status')} />
      </Row>

      <Row alignmentVertical="center" spacing="300">
        <Text type="b300">{t('label_time_period')}</Text>
        <Select value={chartPeriod} width={200} onChange={onChartPeriodChange}>
          {TIME_PERIOD_VALUES.map((value) => (
            <SelectOption key={value} label={t(TIME_PERIOD_KEYS[value])} value={value} />
          ))}
        </Select>
      </Row>

      <GrowthLineChart cumulative={false} data={growthQuery.data?.data ?? []} isLoading={growthQuery.isLoading} title={t('chart_campaigns_created_per_week')} />
      <GrowthLineChart cumulative={true} data={growthQuery.data?.data ?? []} isLoading={growthQuery.isLoading} title={t('chart_total_campaigns_over_time')} />
      <RankedBarChart data={countryData} isLoading={countryQuery.isLoading} title={t('chart_campaigns_by_country')} />
    </Column>
  );
};
